import styled, { css } from 'styled-components';
import { animated } from 'react-spring';
import {
  SPACING_0,
  SPACING_2,
  SPACING_4,
  SPACING_8,
  SPACING_12,
  SPACING_16,
  SPACING_24,
  SPACING_32,
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { RADIUS_CIRCLE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/border-radius';
import { BODY, TITLE_16, CAPTION } from '@nokia-csf-uxr/freeform-design-tokens/tokens/typography';
import { FONT_WEIGHT_REGULAR } from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontWeight';
import { NOKIA_PURE_TEXT, NOKIA_PURE_TEXT_BOLD } from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontFamily';
import { SHADOW_SPREAD_0, SHADOW_SPREAD_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/shadow_spread';
import { TRANSPARENT } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';

import { BORDER_BOX } from 'common';

const BREAKPOINT_7_TO_12 = '@media only screen and (min-width: 768px) and (max-width: 1199px)';
const BREAKPOINT_12PLUS = '@media only screen and (min-width: 1200px)';

export const ICON_SIZE = '1.75rem';
const STEP_BUTTON_SIZE = '1.5rem';
export const BORDER_WIDTH = '0.125rem';
export const LAST_STEP_SELECTOR = 'data-last-step-child';

const SUB_STEP_BUTTON_SIZE = '0.5rem';
const SUB_STEP_BORDER_WIDTH = '0.125rem';

const STEP_HEIGHT_TITLE_ONLY = '3rem';
const STEP_HEIGHT_EXTRA_DETAIL = '3rem';
const STEP_HEIGHT_2_LINES_DETAIL = '4rem';
const STEP_HEIGHT_3_LINES_DETAIL = '5rem';
const VERTICAL_STEP_LINE_HEIGHT = '1.5rem';
const STEP_COMPACT_HEIGHT = '3rem';
const SUB_STEP_HEIGHT = '2rem';
const HORIZONTAL_HEIGHT = '9rem';
const HORIZONTAL_MAX_WIDTH_12 = '22.5rem';
const HORIZONTAL_MAX_WIDTH_7 = '10.25rem';
const HORIZONTAL_MIN_WIDTH = '9.625rem'; // min text width 7.625rem + 2rem padding
const HORIZONTAL_MIN_TEXT_WIDTH = '7.625rem';
const HORIZONTAL_MAX_TEXT_WIDTH = '20.5rem'; // minus 2rem padding

const STEP_LINE_HEIGHT_SUB_STEP = '0.75rem';

const getHeight = ({ $numberOfLines, $isCompact, $isSubStep, $isHorizontal }) => {
  let height;
  switch ($numberOfLines) {
    case 1:
      height = STEP_HEIGHT_EXTRA_DETAIL;
      break;
    case 2:
      height = STEP_HEIGHT_2_LINES_DETAIL;
      break;
    case 3:
      height = STEP_HEIGHT_3_LINES_DETAIL;
      break;
    default:
      height = STEP_HEIGHT_TITLE_ONLY;
      break;
  }
  if ($isCompact) {
    height = STEP_COMPACT_HEIGHT;
  }
  if ($isSubStep) {
    height = SUB_STEP_HEIGHT;
  }
  if ($isHorizontal) {
    height = HORIZONTAL_HEIGHT;
  }
  return css`
      height: ${height};
      min-height: ${height};
      max-height: ${height};
    `;
};

const getInnerStepLineHeight = ({ $numberOfLines, $isSubStep }) => {
  let returnHeight = '100%';
  if ($numberOfLines === 2) {
    returnHeight = '0.875rem';
  } else if ($numberOfLines === 3) {
    returnHeight = '1.875rem';
  }
  if ($isSubStep) {
    returnHeight = '0.625rem';
  }
  return css`
    height: ${returnHeight};
  `;
};

const getTitleColor = ({ $theme, $isPressed, $variant, $isLinear, disabled, $isParent }) => {
  let returnColor = $theme && $theme.text;
  if (!disabled && $isPressed) {
    returnColor = $theme && $theme.pressed.text;
  } else if (disabled || ($variant === 'future' && $isLinear)) {
    returnColor = $theme && $theme.disabled && $theme.disabled.text;
  } else if ($variant === 'completed') {
    returnColor = $theme && $theme.text;
  } else if ($variant === 'current' && !$isParent) {
    returnColor = $theme && $theme.current && $theme.current.text;
  }
  return css`
    color: ${returnColor};
  `;
};

const STEP_STATES = css`
  &:hover {
    background-color: ${({ $theme }) => $theme && $theme.hovered.background};
  }
  &:focus, &:focus-within  {
    outline: none;
    box-shadow: inset ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_2} ${({ $theme }) => $theme && $theme.focused.border};
    background-color: ${({ $theme }) => $theme && $theme.focused.background};
  }
  &:hover:focus {
    background-color: ${({ $theme }) => $theme && $theme.hovered.focused.background};
  }
  &:hover:focus-within {
    background-color: ${({ $theme }) => $theme && $theme.hovered.focused.background};
  }
  ${({ $variant }) => $variant === 'error' && css`
    background-color: ${({ $theme }) => $theme && $theme.error.background};
    &:hover {
      background-color: ${({ $theme }) => $theme && $theme.error.hovered.background};
    }
    &:focus, &:focus-within  {
      background-color: ${({ $theme }) => $theme && $theme.error.background};
    }
    &:hover:focus {
      background-color: ${({ $theme }) => $theme && $theme.error.hovered.focused.background};
    }
    &:hover:focus-within {
      background-color: ${({ $theme }) => $theme && $theme.error.hovered.focused.background};
    }
  `}
  ${({ $variant, $isParent }) => $variant === 'current' && !$isParent && css`
    background-color: ${({ $theme }) => $theme && $theme.selected.background};
    &:hover {
      background-color: ${({ $theme }) => $theme && $theme.selected.hovered.background};
    }
    &:focus, &:focus-within  {
      background-color: ${({ $theme }) => $theme && $theme.selected.focused.background};
    }
    &:hover:focus {
      background-color: ${({ $theme }) => $theme && $theme.selected.hovered.background};
    }
    &:hover:focus-within {
      background-color: ${({ $theme }) => $theme && $theme.selected.hovered.background};
    }
  `}
  ${({ disabled }) => disabled && css`
    pointer-events: none;
    cursor: default;
  `};
`;

const StepForm = styled.div`
  ${BORDER_BOX}
  position: relative;
  padding: ${SPACING_24} ${SPACING_24} 0 ${SPACING_32};
  ${({ rtl }) => rtl && css`
    direction: rtl;
    padding: ${SPACING_24} ${SPACING_32} 0 ${SPACING_24};
  `}
`;

const StepTextContainer = styled.div`
  ${BORDER_BOX}
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start; 
  padding-left: ${SPACING_12};
  padding-bottom: ${SPACING_4};
  ${({ rtl }) => rtl && css`
    direction: rtl;
    padding-left: ${SPACING_0};
    padding-right: ${SPACING_12};
  `}
`;

const StepIndicatorContainer = styled.div`
  ${BORDER_BOX}
  padding-top: ${SPACING_8};
`;

const StepLine = styled.div`
  ${BORDER_BOX}
  width: 0.0625rem;
  min-width: 0.0625rem;
  height: ${VERTICAL_STEP_LINE_HEIGHT};
  background-color: ${({ $theme }) => $theme && $theme.divider};
  margin-left: 2.75rem;
  ${({ $isSubStep }) => $isSubStep && css`
    height: ${STEP_LINE_HEIGHT_SUB_STEP};
    min-height: ${STEP_LINE_HEIGHT_SUB_STEP};
    max-height: ${STEP_LINE_HEIGHT_SUB_STEP};
    margin-top: ${SPACING_0};
    margin-left: 0.21875rem;
  `}
  ${({ rtl }) => rtl && css`
    direction: rtl;
    margin-left: 0;
    margin-right: 2.75rem;
    ${({ $isSubStep }) => $isSubStep && css`
      margin-right: 0.21875rem;
    `}
  `}
`;

const HorizontalStepLine = styled.div`
  ${BORDER_BOX}
  height: 0.0625rem;
  min-height: 0.0625rem;
  width: 100%;
  background-color: ${({ $theme }) => $theme && $theme.divider};
  margin-left: ${SPACING_12};
  ${({ rtl }) => rtl && css`
    margin-left: 0;
    margin-right: ${SPACING_12};
  `}
  flex: 1;
`;

const StepDropDownButton = styled(animated.div)`
  & svg * {
    fill: ${({ $theme }) => $theme && $theme.icon};
  }
  button {
    min-width: 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
    &:hover:not(:disabled) {
      background-color: transparent;
      & svg * {
        fill: ${({ $theme }) => $theme && $theme.icon};
      }
    }
    &:focus:not(:disabled) {
      background-color: transparent;
      box-shadow: none;
      & svg * {
        fill: ${({ $theme }) => $theme && $theme.icon};
      }
    }
  }
  height: 1.5rem;
  width: 1.5rem;
`;

const StepWrapper = styled.li`
  ${BORDER_BOX}
  position: relative;
  cursor: pointer;
  width: 100%;
  ${props => getHeight(props)};
  ${({ $isHorizontal }) => $isHorizontal && css`
    width: auto;
    // smallest breakpoint
    max-width: ${HORIZONTAL_MIN_WIDTH};
    min-width: ${HORIZONTAL_MIN_WIDTH};
    ${BREAKPOINT_12PLUS} {
      max-width: ${HORIZONTAL_MAX_WIDTH_12};
    }
    ${BREAKPOINT_7_TO_12} {
      max-width: ${HORIZONTAL_MAX_WIDTH_7};
    }
  `};
  ${({ $isSubStep }) => !$isSubStep && css`
    &:last-child{
      ${HorizontalStepLine} {
        height: 0;
        min-height: 0;
        max-height: 0;
        width: 0;
        margin: 0;
      }
    }
  `}
  background-color: ${({ $theme }) => $theme && $theme.background};
  ${({ $variant, $isParent }) => $variant === 'current' && !$isParent && css`
    background-color: ${({ $theme }) => $theme && $theme.selected.background};
  `}
  ${({ $variant }) => $variant === 'error' && css`
    background-color: ${({ $theme }) => $theme && $theme.error.background};
  `}
  ${props => props.$hoverEffect && css` ${STEP_STATES}`}
  ${({ $isHorizontal }) => $isHorizontal && css`
    &:focus, &:focus-within  {
      ${HorizontalStepLine} {
        ${({ rtl }) => !rtl && css`
          margin-right: ${SPACING_2};
        `};
        ${({ rtl }) => rtl && css`
          margin-left: ${SPACING_2};
        `};
      }
    }
  `};
  ${({ $isHorizontal }) => !$isHorizontal && css`
    ${StepLine} {
      height: 100%;
    }
    &:focus, &:focus-within  {
      ${StepLine} {
       margin-bottom: ${SPACING_2};
       ${props => getInnerStepLineHeight(props)};
       ${({ $isSubStep }) => $isSubStep && css`
          min-height: 0.625rem;
          max-height: 0.625rem;
          margin-bottom: 0;
       `}
      }
    }
  `};
`;

const Step = styled.div`
  ${BORDER_BOX}
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100%;
  width: 100%;
  &:focus-visible {
    outline: none;
  }; 
  padding: ${SPACING_4} ${SPACING_24} ${SPACING_24} ${SPACING_32};
  ${({ rtl }) => rtl && css`
    direction: rtl;
    padding: ${SPACING_4} ${SPACING_32} ${SPACING_24} ${SPACING_24};
  `}
  padding-bottom: 0;
  ${({ $hasDetails }) => !$hasDetails && css`
    padding-top: ${SPACING_12};
    ${StepIndicatorContainer} {
      padding-top: ${SPACING_0};
    }
    ${StepTextContainer} {
      padding-bottom: ${SPACING_12};
    }
  `}
  ${({ $isCompact }) => $isCompact && css`
    padding-top: ${SPACING_12};
    ${StepIndicatorContainer} {
      padding-top: ${SPACING_0};
    }
    ${StepTextContainer} {
      padding-bottom: ${SPACING_12};
    }
  `}
  ${({ $isParent }) => $isParent && css`
    padding-left: ${SPACING_8};
    ${({ rtl }) => rtl && css`
      padding-left: ${SPACING_24};
      padding-right: ${SPACING_8};
    `}
    ${({ $numberOfLines }) => $numberOfLines > 1 && css`
      ${StepDropDownButton} {
       margin-top: ${SPACING_8};
      }
    `}
  `}
`;

const StepDetail = styled.div`
  ${BORDER_BOX}
  width: 100%;
  max-width: 100%;
  position: relative;
  ${CAPTION}
  ${props => getTitleColor(props)};
`;

const HorizontalStep = styled.div`
  ${BORDER_BOX}
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  width: 100%;
  padding-top: ${SPACING_24};
  padding-left: ${SPACING_12};
  padding-bottom: 0;
  &:focus-visible {
    outline: none;
  }; 
  ${({ rtl }) => rtl && css`
    direction: rtl;
    padding-left: 0;
    padding-right: ${SPACING_12};
  `}
  ${StepTextContainer} {
    padding-bottom: 0;
    padding-left: 0;
    padding-right: ${SPACING_32};
    max-width: 100%;
    ${({ rtl }) => rtl && css`
      padding-left: ${SPACING_32};
      padding-right: 0;
    `}
  }
  ${StepDetail} {
    min-width: ${HORIZONTAL_MIN_TEXT_WIDTH};
    max-width: ${HORIZONTAL_MAX_TEXT_WIDTH};
  }
  ${StepIndicatorContainer} {
    padding-top: ${SPACING_0};
  }
`;

const SubStep = styled.div`
  ${BORDER_BOX}
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  height: 100%;
  width: 100%;
  &:focus-visible {
    outline: none;
  }; 
  padding: ${SPACING_0} ${SPACING_24} ${SPACING_24} 2.5rem;
  ${props => getTitleColor(props)};
  ${({ rtl }) => rtl && css`
    direction: rtl;
    padding: ${SPACING_0} 2.5rem ${SPACING_24} ${SPACING_24};
  `}
  padding-bottom: 0;
  width: 100%;
  ${StepIndicatorContainer} {
    padding: ${SPACING_0};
  }
`;

const StepTitle = styled.div`
  ${BORDER_BOX}
  position: relative;
  ${TITLE_16}
  font-weight: ${FONT_WEIGHT_REGULAR};
  font-family:  ${NOKIA_PURE_TEXT};
  ${({ $variant }) => $variant === 'current' && css`
    ${TITLE_16}
  `};
  ${({ $isSubStep }) => $isSubStep && css`
    ${BODY}
    ${({ $variant }) => $variant === 'current' && css`
      font-family: ${NOKIA_PURE_TEXT_BOLD};
   `};
  `} 
  ${props => getTitleColor(props)};
  width: 100%;
  max-width: 100%;
`;

const StepIndicator = styled.div`
  ${BORDER_BOX}
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${STEP_BUTTON_SIZE};
  min-height: ${STEP_BUTTON_SIZE};
  max-height: ${STEP_BUTTON_SIZE};
  width: ${STEP_BUTTON_SIZE};
  min-width: ${STEP_BUTTON_SIZE};
  max-width: ${STEP_BUTTON_SIZE};
  border-radius: ${RADIUS_CIRCLE};
  ${BODY}
  background-color: ${({ $theme }) => $theme && $theme.BUTTON.future.background};
  color: ${({ $theme }) => $theme && $theme.BUTTON.future.text};
  border: ${BORDER_WIDTH} solid ${({ $theme }) => $theme && $theme.BUTTON.future.border};
  ${({ $isNonLinear }) => $isNonLinear && css`
    border: ${BORDER_WIDTH} solid ${({ $theme }) => $theme && $theme.BUTTON.future.borderNonLinear};
  `};
  ${({ disabled }) => disabled && css`
    color: ${({ $theme }) => $theme && $theme.disabled.text};
    border: ${BORDER_WIDTH} solid ${({ $theme }) => $theme && $theme.disabled.border};
  `};
  ${({ $variant }) => $variant === 'current' && css`
    background-color: ${({ $theme }) => $theme && $theme.BUTTON.current.icon};
    color: ${({ $theme }) => $theme && $theme.BUTTON.current.text};
    border: ${BORDER_WIDTH} solid ${({ $theme }) => $theme && $theme.BUTTON.current.icon};
  `};
  ${({ rtl }) => rtl && css`
    direction: rtl;
  `}
`;

const StepIndicatorInside = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const SubStepIndicator = styled.div`
  ${BORDER_BOX}
  ${BORDER_BOX}
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${SUB_STEP_BUTTON_SIZE};
  min-height: ${SUB_STEP_BUTTON_SIZE};
  max-height: ${SUB_STEP_BUTTON_SIZE};
  width: ${SUB_STEP_BUTTON_SIZE};
  min-width: ${SUB_STEP_BUTTON_SIZE};
  max-width: ${SUB_STEP_BUTTON_SIZE};
  border-radius: ${RADIUS_CIRCLE};
  background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.future.background}; // variant future default
  border: ${SUB_STEP_BORDER_WIDTH} solid ${({ $theme }) => $theme && $theme.SUBSTEP.future.border};
  margin: 0 ${SPACING_32} 0 0;
  ${({ rtl }) => rtl && css`
    direction: rtl;
    margin: 0 0 0 ${SPACING_32};
  `}
  ${({ disabled }) => disabled && css`
    border: ${SUB_STEP_BORDER_WIDTH} solid ${({ $theme }) => $theme && $theme.SUBSTEP.disabled.border};
  `};
  ${({ $variant }) => $variant === 'current' && css`
    background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.current.background};
    border: none;
  `};
  ${({ $variant }) => $variant === 'error' && css`
    background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.error.background};
    ${({ disabled }) => disabled && css`
      background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.error.disabled};
    `};
    border: none;
  `};
  ${({ $variant }) => $variant === 'warning' && css`
    background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.warning.background};
    ${({ disabled }) => disabled && css`
      background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.warning.disabled};
    `};
    border: none;
  `};
  ${({ $variant }) => $variant === 'completed' && css`
    background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.completed.background};
    ${({ disabled }) => disabled && css`
      background-color: ${({ $theme }) => $theme && $theme.SUBSTEP.completed.disabled};
    `};
    border: none;
  `};
`;

const StepConfigurationArea = styled.div`
  ${BORDER_BOX}
  display: flex;
  flex-flow: row;
  flex-direction: column;
  height: 100%;
  padding: ${SPACING_16};
  overflow: auto;
`;

const StepFormArea = styled.div`
  ${BORDER_BOX}
  display: flex;
  flex-direction: row;
  width: 100%;
  overflow: auto;
`;

const SubStepArea = styled.div`
  ${({ $rtl }) =>
    $rtl &&
    css`
      direction: rtl;
    `}
  height: 0;
  overflow: hidden;
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      height: auto;
    `};
`;

const AnimatedSubStep = styled.div`
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  ${({ $isHorizontal }) => $isHorizontal && css`
    display: flex;
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
    position: relative;
    justify-content: flex-start;
    ${({ $rtl }) => $rtl && css`
        direction: rtl;
    `}
  `}
`;

const InnerSteps = styled.div`
  ${({ $isHorizontal }) => $isHorizontal && css`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    white-space: nowrap;
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
      height: 0;
      width: 0;
    }
  `}
  ${({ $isHorizontal }) => !$isHorizontal && css`
    // last step does not have a line
    & [data-step=${LAST_STEP_SELECTOR}] + ${StepLine} {
        height: 0;
        min-height: 0;
        max-height: 0;
        width: 0;
        margin: 0;
      }
    }
  `}
`;

const ScrollButtonContainer = styled.div`
  ${BORDER_BOX}
  z-index: 1;
  display: flex;
  align-items: center;
  margin-top: ${SPACING_8}; // to not overwrite the toolbar shadow
  margin-bottom: ${SPACING_8} // to center the scroll button icon
  ${({ left }) => left && css`
    background-image: linear-gradient(to left,${TRANSPARENT} 0%, ${({ $theme }) => $theme.scrollButton.background} 30%);
    padding-left: ${SPACING_4};
    padding-right: ${SPACING_4};
  `}
  ${({ right }) => right && css`
    background-image: linear-gradient(to right,${TRANSPARENT} 0%, ${({ $theme }) => $theme.scrollButton.background} 30%);
    padding-right: ${SPACING_4};
    padding-left: ${SPACING_4};
  `}
`;

const styles = {
  StepForm,
  StepWrapper,
  Step,
  HorizontalStep,
  SubStep,
  StepTextContainer,
  StepTitle,
  StepDetail,
  StepIndicatorContainer,
  StepIndicator,
  StepIndicatorInside,
  StepLine,
  HorizontalStepLine,
  SubStepIndicator,
  StepConfigurationArea,
  StepFormArea,
  StepDropDownButton,
  SubStepArea,
  AnimatedSubStep,
  ScrollButtonContainer,
  ScrollContainer,
  InnerSteps,
};

export default styles;